import { useMemo } from 'react';

import { Notification } from '@wavingroup/iot-core/gen/ts/wavin/blaze/v1/notification_pb';
import type { ParsedNotification } from '../types';
import {
  getNotificationContent,
  SentioNotificationData,
} from '../utils/notificationContent';
import { getNotificationType } from '../utils/categoryMapping';
import { formatTimestampRelative } from '~/shared/utils/formatTimestamp';
import { mapSeverityToUrgency } from '../utils/severietyMapping';

const ROOM_ID_REGEX = /rooms\/([^/]+)/;
const DHW_UNIT_ID_REGEX = /domestic-hot-water-units\/([^/]+)/;
const VENTILATION_ID_REGEX = /ventilation-controls\/([^/]+)/;
const DEVICE_RESOURCE_REGEX = /^(devices\/[^/]+)/;

type TitledResource = {
  titlePersonalized?: string;
  title?: string;
};

const getResourceTitle = (resource?: TitledResource): string =>
  resource?.titlePersonalized || resource?.title || '';

const findById = <T extends { id: string }>(
  items: T[] | undefined,
  id: string | undefined,
): T | undefined => (id ? items?.find((item) => item.id === id) : undefined);

export function useParsedNotifications(notifications: Notification[], t: any) {
  const parsedNotifications = useMemo(
    () =>
      notifications.map((notification): ParsedNotification => {
        const sentioData =
          notification.data.case === 'sentioNotification'
            ? (notification.data.value as SentioNotificationData)
            : undefined;
        const deviceConfig = sentioData?.deviceConfig;
        const notificationType = getNotificationType(notification.category);
        const isResolved = notification.resolvedAt != null;
        const timestampOptions = { includeTime: true };
        const createdDate = formatTimestampRelative(
          notification.createTime,
          timestampOptions,
        );
        const resolvedDate = isResolved
          ? formatTimestampRelative(notification.resolvedAt, timestampOptions)
          : undefined;
        const date = createdDate;
        const targetResourceName =
          notification.data.value?.targetResourceName ?? '';
        const roomId = targetResourceName.match(ROOM_ID_REGEX)?.[1];

        const roomTitle =
          getResourceTitle(findById(deviceConfig?.rooms, roomId)) ||
          getResourceTitle(
            findById(
              deviceConfig?.domesticHotWaterUnits,
              targetResourceName.match(DHW_UNIT_ID_REGEX)?.[1],
            ),
          ) ||
          getResourceTitle(
            findById(
              deviceConfig?.ventilationControls,
              targetResourceName.match(VENTILATION_ID_REGEX)?.[1],
            ),
          );

        const content = getNotificationContent(sentioData, roomTitle, t);
        return {
          title: content.title,
          deviceTitle: getResourceTitle(deviceConfig),
          roomTitle,
          alertDescription: content.alertDescription,
          stepsToPerform: content.stepsToPerform,
          date,
          resolvedDate,
          urgency: mapSeverityToUrgency(notification.severity),
          isResolved,
          roomId: roomId ?? '',
          deviceName:
            targetResourceName.match(DEVICE_RESOURCE_REGEX)?.[1] || '',
          readState: notification.readState,
          name: notification.name,
          category: notificationType.type,
          categoryRawType: notification.category,
          categoryLabel: notificationType.translation,
        };
      }),
    [notifications, t],
  );

  return parsedNotifications;
}
